import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { timeEntriesApi } from '../api/timeEntries';
import { toast } from 'sonner';
import { invalidateScoped, tenantKey } from '../lib/queryKeys';
import { useCompanyStore } from '../store/company';

export const useTimeEntries = (taskId, enabled = true) => {
  const companyId = useCompanyStore(state => state.companyId);
  return useQuery({
    queryKey: tenantKey(companyId, 'timeEntries', String(taskId ?? '')),
    queryFn: () => timeEntriesApi.getByTask(taskId),
    enabled: enabled && !!taskId,
    staleTime: 30 * 1000, // 30 segundos
  });
};

export const useActiveTimer = (enabled = true) => {
  const companyId = useCompanyStore(state => state.companyId);
  return useQuery({
    queryKey: tenantKey(companyId, 'activeTimer'),
    queryFn: () => timeEntriesApi.getActive(),
    enabled,
    // Intervalo dinâmico: mais frequente quando há timer ativo, bem menos quando não há
    // Retorna false para desabilitar se o hook estiver desativado
    refetchInterval: query => {
      if (!enabled) return false;
      const hasActive = !!query?.state?.data?.active_timer;
      return hasActive ? 5000 : 30000;
    },
    refetchIntervalInBackground: false, // não pollar quando a aba estiver em background
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 0,
    // Não insistir em caso de 429 e usar backoff curto para outros erros
    retry: (failureCount, error) => {
      const status = error?.response?.status;
      if (status === 429) return false;
      return failureCount < 2;
    },
    retryDelay: attempt => Math.min(30000, 5000 * attempt), // 5s, 10s, máx 30s
  });
};

export const useStartTimer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, notes }) => timeEntriesApi.start(taskId, notes),
    onSuccess: async data => {
      toast.success('Timer iniciado com sucesso');
      // Sincronizar imediatamente o estado do timer ativo
      await Promise.all([
        invalidateScoped(queryClient, 'activeTimer'),
        invalidateScoped(queryClient, 'timeEntries'),
      ]);
      await queryClient.refetchQueries({
        predicate: query => Array.isArray(query.queryKey) && query.queryKey.includes('activeTimer'),
        type: 'active',
      });
    },
    onError: async error => {
      const message = error.response?.data?.message || 'Erro ao iniciar timer';
      toast.error(message);
      // Se o backend já iniciou ou havia um timer ativo, garantir sincronização imediata
      await queryClient.refetchQueries({
        predicate: query => Array.isArray(query.queryKey) && query.queryKey.includes('activeTimer'),
        type: 'active',
      });
    },
  });
};

export const useStopTimer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: timeEntryId => timeEntriesApi.stop(timeEntryId),
    onSuccess: async data => {
      toast.success('Timer parado com sucesso');
      // Sincronizar imediatamente o estado do timer ativo
      await Promise.all([
        invalidateScoped(queryClient, 'activeTimer'),
        invalidateScoped(queryClient, 'timeEntries'),
        // Atualizar boards e estatísticas relacionadas
        invalidateScoped(queryClient, 'board'),
        invalidateScoped(queryClient, 'board-stats'),
        invalidateScoped(queryClient, 'weekly-hours-report'),
      ]);
      await queryClient.refetchQueries({
        predicate: query => Array.isArray(query.queryKey) && query.queryKey.includes('activeTimer'),
        type: 'active',
      });
    },
    onError: async error => {
      const message = error.response?.data?.message || 'Erro ao parar timer';
      toast.error(message);
      // Forçar sincronização para refletir estado real do servidor
      await queryClient.refetchQueries({
        predicate: query => Array.isArray(query.queryKey) && query.queryKey.includes('activeTimer'),
        type: 'active',
      });
    },
  });
};

export const useCreateTimeEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: data => timeEntriesApi.create(data),
    onSuccess: data => {
      toast.success('Entrada de tempo criada com sucesso');
      invalidateScoped(queryClient, 'timeEntries');
      invalidateScoped(queryClient, 'weekly-hours-report');
    },
    onError: error => {
      const message = error.response?.data?.message || 'Erro ao criar entrada de tempo';
      toast.error(message);
    },
  });
};

export const useUpdateTimeEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ timeEntryId, data }) => timeEntriesApi.update(timeEntryId, data),
    onSuccess: data => {
      toast.success('Entrada de tempo atualizada com sucesso');
      invalidateScoped(queryClient, 'timeEntries');
      invalidateScoped(queryClient, 'weekly-hours-report');
    },
    onError: error => {
      const message = error.response?.data?.message || 'Erro ao atualizar entrada de tempo';
      toast.error(message);
    },
  });
};

export const useDeleteTimeEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: timeEntryId => timeEntriesApi.delete(timeEntryId),
    onSuccess: data => {
      toast.success('Entrada de tempo deletada com sucesso');
      invalidateScoped(queryClient, 'timeEntries');
      invalidateScoped(queryClient, 'weekly-hours-report');
    },
    onError: error => {
      const message = error.response?.data?.message || 'Erro ao deletar entrada de tempo';
      toast.error(message);
    },
  });
};
